/**
 * UpdateBanner — slim, non-intrusive bar shown when a newer SPA build has been
 * deployed while the current tab was open.
 *
 * Wiring: self-contained. It drives {@link useVersionCheck} internally, so the
 * shell only has to render `<UpdateBanner />` once. When the deployed
 * `version.json` differs from the version this tab booted with, the bar slides
 * in with a "Rafraîchir" action that hard-reloads onto the new build.
 *
 * Dismissible: the user can close it; it re-appears only when an *even newer*
 * version is subsequently detected (we remember which version was dismissed).
 *
 * Themed via the semantic design tokens (light/dark), responsive down to 360px
 * (content wraps, no horizontal overflow), and animation is disabled under
 * `prefers-reduced-motion`.
 */

import { useEffect, useState } from 'react';

import { hardReload, useVersionCheck } from '@/app/useVersionCheck';
import { useT } from '@/i18n';

export function UpdateBanner() {
  const { t } = useT();
  const { updateAvailable, latestVersion } = useVersionCheck();
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [entered, setEntered] = useState(false);

  // Visible when an update is available AND the detected version has not been
  // dismissed. A newer version (!= the dismissed one) re-shows the bar.
  const visible = updateAvailable && latestVersion !== null && latestVersion !== dismissedVersion;

  // Trigger the slide/fade-in transition once the bar is in the DOM.
  useEffect(() => {
    if (!visible) {
      setEntered(false);
      return undefined;
    }
    const id = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(id);
  }, [visible]);

  if (!visible) return null;

  const onRefresh = () => {
    setRefreshing(true);
    void hardReload();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={
        'z-30 shrink-0 border-b border-primary/30 bg-primary/10 ' +
        'transition-[opacity,transform] duration-300 ease-out ' +
        'motion-reduce:transition-none ' +
        (entered ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0')
      }
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 sm:px-4">
        <span
          aria-hidden="true"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary"
        >
          {/* sparkle / upgrade glyph */}
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 3l1.6 3.6L17 8l-3.4 1.4L12 13l-1.6-3.6L7 8l3.4-1.4z" />
            <path d="M18 14l.7 1.6L20 16l-1.3.4L18 18l-.7-1.6L16 16l1.3-.4z" />
          </svg>
        </span>

        <p className="min-w-0 flex-1 text-sm text-foreground">
          <span className="font-medium">{t('shell.app.updateBanner.aNewVersionIsAvailable')}</span>{' '}
          <span className="text-muted">{t('shell.app.updateBanner.refreshToLoadTheLatestUpdate')}</span>
        </p>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-token bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-70"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className={`h-4 w-4 ${refreshing ? 'animate-spin motion-reduce:animate-none' : ''}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6" />
            </svg>
            {refreshing ? t('shell.app.updateBanner.refreshing') : t('shell.app.updateBanner.refresh')}
          </button>
          <button
            type="button"
            onClick={() => latestVersion && setDismissedVersion(latestVersion)}
            aria-label={t('shell.app.updateBanner.dismissThisUpdateNotification')}
            className="rounded-token p-1.5 text-muted transition-colors hover:bg-primary/10 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default UpdateBanner;
